import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faCheckCircle,
  faForwardStep,
  faChevronDown,
  faLink,
  faFileAlt,
  faCalendarAlt,
  faStar,
  faCircleExclamation,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import CourseKindTag, { kindOf, kindCardClass } from '../components/CourseKind/CourseKind';
import ClassStats from '../components/CourseKind/ClassStats';
import './MyClasses.css';

const DEFAULT_IMG = '/assets/coursesimages/default-course.jpg';

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
};

// Same rule as before: a module's progress counts its current level (8 / 16 / 24 lessons)
const levelLimit = (count) => {
  if (count > 8 && count < 16) return 8;
  if (count > 16 && count < 24) return 16;
  if (count > 24) return 24;
  return count;
};

const Stars = ({ rating }) => {
  const r = Math.round(Number(rating) || 0);
  if (r <= 0) return null;
  return (
    <span className="rmmc-stars" aria-label={`Rated ${r} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FontAwesomeIcon key={n} icon={faStar} className={n <= r ? 'is-on' : ''} />
      ))}
    </span>
  );
};

const MyClasses = ({ userData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({}); // moduleName -> bool

  const studentId = userData?.id;

  // ---------- Data (same endpoint) ----------
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/student/studentclasses/${studentId}`);
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) setError('We couldn’t load your classes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // ---------- Group lessons by module, with progress ----------
  const modules = useMemo(() => {
    if (!data?.myclasses) return [];
    const done = new Map(); // IdClass -> completions[]
    (data.myallclassesmade || []).forEach((c) => {
      if (!done.has(c.IdClass)) done.set(c.IdClass, []);
      done.get(c.IdClass).push(c);
    });

    const groups = [];
    const byName = {};
    data.myclasses.forEach((lesson) => {
      if (!byName[lesson.ModuleName]) {
        byName[lesson.ModuleName] = { name: lesson.ModuleName, lessons: [] };
        groups.push(byName[lesson.ModuleName]);
      }
      byName[lesson.ModuleName].lessons.push({ ...lesson, completions: done.get(lesson.Id) || [] });
    });

    return groups.map((g) => {
      const total = levelLimit(g.lessons.length);
      // any project of the module counts (a skipped project 8 replaced by project 9 is still 8/8),
      // never more than the module's level (8 / 16 / 24)
      const completed = Math.min(total, g.lessons.filter((l) => l.completions.length > 0).length);
      const percent = total ? Math.round((completed / total) * 100) : 0;
      const nextIndex = g.lessons.findIndex((l) => l.completions.length === 0);
      const state = total && completed === total ? 'done' : completed > 0 ? 'progress' : 'new';
      return { ...g, total, completed, percent, nextIndex, state };
    });
  }, [data]);

  // Open the module the student is working on (first in progress, else first not finished)
  useEffect(() => {
    if (!modules.length) return;
    const current = modules.find((m) => m.state === 'progress') || modules.find((m) => m.state !== 'done') || modules[0];
    setOpen({ [current.name]: true });
  }, [modules]);

  const toggle = (name) => setOpen((prev) => ({ ...prev, [name]: !prev[name] }));

  // ---------- Loading ----------
  if (loading || !studentId) {
    return (
      <div className="rmmc" aria-busy="true">
        <div className="rmmc-inner">
          <div className="rmmc-skel rmmc-skel--title" />
          <div className="rmmc-stats">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmmc-skel rmmc-skel--stat" />
            ))}
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="rmmc-skel rmmc-skel--row" />
          ))}
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error) {
    return (
      <div className="rmmc">
        <div className="rmmc-state">
          <FontAwesomeIcon icon={faCircleExclamation} className="rmmc-state-icon is-error" />
          <h1 className="rmmc-state-title">Something went wrong</h1>
          <p>{error} Please try again in a moment.</p>
          <button type="button" className="rmmc-btn" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ---------- Empty ----------
  if (!modules.length) {
    return (
      <div className="rmmc">
        <div className="rmmc-state">
          <FontAwesomeIcon icon={faBook} className="rmmc-state-icon" />
          <h1 className="rmmc-state-title">No classes yet</h1>
          <p>Your course lessons will appear here once your first class is scheduled.</p>
        </div>
      </div>
    );
  }

  // overall = the modules' levels (8 / 16 / 24), not every extra project of a module
  const overallTotal = modules.reduce((n, m) => n + m.total, 0);
  const overallDone = modules.reduce((n, m) => n + m.completed, 0);
  const percent = overallTotal
    ? Math.round((overallDone / overallTotal) * 100)
    : Math.max(0, Math.min(100, Number(data.percent) || 0));

  return (
    <div className="rmmc">
      <div className="rmmc-inner">
        <header className="rmmc-head">
          <span className="rmmc-eyebrow">My Classes</span>
          <h1 className="rmmc-title">
            Your <span>learning path</span>
          </h1>
          <p className="rmmc-sub">Every lesson, project and note from your classes, in one place.</p>
        </header>

        {/* ---------- Stats ---------- */}
        <ClassStats
        percent={percent}
        lessonsDone={overallTotal ? overallDone : data.nbofprojectsdone || 0}
        lessonsTotal={overallTotal || data.totalprojects || 0}
        classesTaken={data.nbofclasstaken || 0}
        classesTotal={data.nbofclass || 0}
        sessionTypes={data.sessionTypes}
      />

        {/* ---------- Modules ---------- */}
        <div className="rmmc-modules">
          {modules.map((m) => {
            const isOpen = Boolean(open[m.name]);
            const panelId = `rmmc-panel-${m.name.replace(/[^a-z0-9]+/gi, '-')}`;
            return (
              <section key={m.name} className={`rmmc-module is-${m.state} ${isOpen ? 'is-open' : ''} ${kindCardClass(kindOf(m.lessons))}`}>
              <CourseKindTag kind={kindOf(m.lessons)} />
                <h2 className="rmmc-module-h">
                  <button
                    type="button"
                    className="rmmc-module-head"
                    onClick={() => toggle(m.name)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span className={`rmmc-badge is-${m.state}`}>
                      {m.state === 'done' ? 'Completed' : m.state === 'progress' ? 'In progress' : 'Not started'}
                    </span>
                    <span className="rmmc-module-name">{m.name}</span>
                    <span className="rmmc-module-progress">
                      <span className="rmmc-bar" aria-hidden="true">
                        <span style={{ width: `${m.percent}%` }} />
                      </span>
                      <span className="rmmc-module-count">
                        {m.completed}/{m.total}
                      </span>
                    </span>
                    <FontAwesomeIcon icon={faChevronDown} className="rmmc-chev" />
                  </button>
                </h2>

                {isOpen && (
                  <ol id={panelId} className="rmmc-lessons">
                    {m.lessons.map((l, i) => {
                      const isDone = l.completions.length > 0;
                      // skipped = not done in a class (counts for the course): shown in grey
                      const isSkipped = isDone && l.completions.every((c) => Number(c.Skipped) === 1);
                      const isNext = i === m.nextIndex;
                      const last = l.completions[l.completions.length - 1];
                      return (
                        <li
                          key={`${l.Id}-${i}`}
                          className={`rmmc-lesson ${isDone ? 'is-done' : ''} ${isSkipped ? 'is-skipped' : ''} ${isNext ? 'is-next' : ''}`}
                        >
                          <span className="rmmc-num" aria-hidden="true">
                            {isSkipped ? <FontAwesomeIcon icon={faForwardStep} /> : isDone ? <FontAwesomeIcon icon={faCheckCircle} /> : i + 1}
                          </span>

                          <img
                            className="rmmc-thumb"
                            src={l.CourseFilesLink || DEFAULT_IMG}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              if (!e.currentTarget.src.endsWith(DEFAULT_IMG)) e.currentTarget.src = DEFAULT_IMG;
                            }}
                          />

                          <div className="rmmc-lesson-body">
                            <h3>
                              {(l.CourseName || '').trim()}
                              {isNext && (
                                <span className="rmmc-next-tag">
                                  <FontAwesomeIcon icon={faPlay} /> Next
                                </span>
                              )}
                            </h3>

                            {isSkipped && <p className="rmmc-meta">Skipped</p>}
                            {isDone && !isSkipped && last && (
                              <p className="rmmc-meta">
                                <FontAwesomeIcon icon={faCalendarAlt} /> {fmtDate(last.ClassDateTime)}
                                {(last.FirstName || last.LastName) && (
                                  <> · with {[last.FirstName, last.LastName].filter(Boolean).join(' ')}</>
                                )}
                                <Stars rating={last.Rating} />
                              </p>
                            )}
                            {isDone && l.completions.length > 1 && (
                              <p className="rmmc-meta rmmc-meta--small">Taken {l.completions.length} times</p>
                            )}
                          </div>

                          {isDone && (l.CourseLink || l.CourseNote) && (
                            <div className="rmmc-actions">
                              {l.CourseLink && (
                                <a href={l.CourseLink} target="_blank" rel="noopener noreferrer">
                                  <FontAwesomeIcon icon={faLink} /> Project
                                </a>
                              )}
                              {l.CourseNote && (
                                <a href={l.CourseNote} target="_blank" rel="noopener noreferrer">
                                  <FontAwesomeIcon icon={faFileAlt} /> Notes
                                </a>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MyClasses;